<template>
  <div
    :class="[
      'compact-condition-editor',
      { 'compact-condition-editor--child': isSubCondition },
    ]">
    <div class="compact-condition-editor__body">
      <div class="compact-condition-editor__line">
        <span
          :class="[
            'compact-condition-editor__drag-handle',
            dragHandleClass,
          ]"
          title="Arrastar condição">
          <v-icon small color="primary">fas fa-grip-vertical</v-icon>
        </span>

        <div class="compact-condition-editor__relation">
          <v-btn-toggle
            v-if="showTypeControl"
            v-model="condition.type"
            dense
            mandatory
            class="compact-condition-editor__type-toggle">
            <v-btn
              v-for="conditionType in conditionTypeItems"
              :key="conditionType"
              :value="conditionType"
              small
              active-class="primary foreground-accent--text"
              min-width="38">
              {{ formatConditionType(conditionType) }}
            </v-btn>
          </v-btn-toggle>

          <span
            v-else-if="isSubCondition"
            class="compact-condition-editor__type-fixed">
            E
          </span>
        </div>

        <v-autocomplete
          v-model="condition.dataSource"
          :items="datasource"
          item-text="label"
          item-value="value"
          label="Origem"
          outlined
          dense
          hide-details
          color="primary"
          class="compact-condition-editor__source"
        />

        <ConditionField
          v-if="firstField"
          :field="firstField"
          :condition="condition"
          :field-types="fieldTypes"
          :field-functions="fieldFunctions"
          :datasource-columns="datasourceColumns"
          :constants="constants"
          :default-value="defaultValue"
          compact
          class="compact-condition-editor__field"
        />

        <v-autocomplete
          v-model="condition.operator"
          :items="operators"
          item-text="label"
          item-value="value"
          label="Operador"
          outlined
          dense
          hide-details
          color="primary"
          class="compact-condition-editor__operator"
          @change="handleOperatorChange"
        />

        <ConditionField
          v-if="showFieldTwo && secondField"
          :field="secondField"
          :condition="condition"
          :field-types="fieldTypes"
          :field-functions="fieldFunctions"
          :datasource-columns="datasourceColumns"
          :constants="constants"
          :default-value="defaultValue"
          compact
          class="compact-condition-editor__field"
        />

        <ConditionField
          v-if="showFieldThree && thirdField"
          :field="thirdField"
          :condition="condition"
          :field-types="fieldTypes"
          :field-functions="fieldFunctions"
          :datasource-columns="datasourceColumns"
          :constants="constants"
          :default-value="defaultValue"
          compact
          class="compact-condition-editor__field compact-condition-editor__field--third"
        />

        <div class="compact-condition-editor__actions">
          <v-btn
            v-if="hasSubConditions"
            icon
            small
            color="primary"
            :title="subConditionsToggleTitle"
            @click.stop="toggleSubConditions">
            <v-icon small>
              {{ subConditionsExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}
            </v-icon>
          </v-btn>

          <v-btn
            v-if="!isSubCondition"
            icon
            small
            color="primary"
            title="Adicionar sub-condição"
            @click="addSubCondition">
            <v-icon small>fa-plus</v-icon>
          </v-btn>

          <v-btn
            v-if="canRemove"
            icon
            small
            color="error"
            title="Remover condição"
            @click="$emit('remove', condition.id)">
            <v-icon small>far fa-trash-alt</v-icon>
          </v-btn>
        </div>
      </div>

      <div
        v-if="hasSubConditions"
        v-show="!subConditionsExpanded"
        class="compact-condition-editor__children-summary"
        @click.stop="toggleSubConditions">
        {{ subConditionsSummary }}
      </div>

      <div
        v-if="hasSubConditions"
        v-show="subConditionsExpanded"
        class="compact-condition-editor__children">
        <draggable
          :key="childrenDraggableKey"
          v-model="condition.children"
          :handle="`.${childDragHandleClass}`"
          ghost-class="compact-condition-editor--ghost"
          :animation="180"
          @change="reorderSubConditions"
          @end="reorderSubConditions">
          <CompactConditionEditor
            v-for="(subCondition, subConditionIndex) in condition.children"
            :key="`compact-child-${subCondition.id || 'new'}-${subConditionIndex}`"
            :condition="subCondition"
            :index="subConditionIndex"
            layout="compact"
            :condition-types="conditionTypes"
            :datasource="datasource"
            :field-types="fieldTypes"
            :field-functions="fieldFunctions"
            :constants="constants"
            :operators="operators"
            :default-value="defaultValue"
            :can-remove="true"
            :is-sub-condition="true"
            :drag-handle-class="childDragHandleClass"
            @remove="removeSubCondition"
          />
        </draggable>
      </div>
    </div>
  </div>
</template>

<script>
import { Condition as ConditionType, Field } from '@/store/modules/rule/rule-types';
import draggable from 'vuedraggable';
import ConditionField from './ConditionField.vue';

export default {
  name: 'CompactConditionEditor',

  components: { ConditionField, draggable },

  props: {
    condition: {
      type: Object,
      required: true,
    },

    index: {
      type: Number,
      default: 0,
    },

    layout: {
      type: String,
      default: 'compact',
    },

    conditionTypes: {
      type: Array,
      default: () => [],
    },

    datasource: {
      type: Array,
      default: () => [],
    },

    fieldTypes: {
      type: Array,
      default: () => [],
    },

    fieldFunctions: {
      type: Array,
      default: () => [],
    },

    constants: {
      type: Array,
      default: () => [],
    },

    operators: {
      type: Array,
      default: () => [],
    },

    defaultValue: {
      type: [String, Number],
      default: '',
    },

    canRemove: {
      type: Boolean,
      default: false,
    },

    isSubCondition: {
      type: Boolean,
      default: false,
    },

    isFirstCondition: {
      type: Boolean,
      default: false,
    },

    dragHandleClass: {
      type: String,
      default: 'compact-condition-editor__drag-handle--root',
    },
  },

  data: () => ({ subConditionsExpanded: true }),

  computed: {
    showTypeControl() {
      return !this.isSubCondition && !this.isFirstCondition;
    },

    conditionTypeItems() {
      return this.conditionTypes.filter((conditionType) => conditionType);
    },

    childDragHandleClass() {
      return `compact-condition-editor__drag-handle--children-${this.condition.id || this.index + 1}`;
    },

    childrenDraggableKey() {
      const childrenLength = this.condition.children ? this.condition.children.length : 0;

      return `compact-condition-editor-children-${this.condition.id || this.index + 1}-${childrenLength}`;
    },

    showFieldTwo() {
      return this.operators.some((operator) => operator.value === this.condition.operator
        && (operator.type === 'TERNARY' || operator.type === 'BINARY'));
    },

    showFieldThree() {
      return this.operators.some((operator) => operator.value === this.condition.operator
        && operator.type === 'TERNARY');
    },

    datasourceColumns() {
      const selectedDatasource = this.datasource
        .find((item) => item.value === this.condition.dataSource);

      return selectedDatasource && selectedDatasource.columns
        ? selectedDatasource.columns
        : [];
    },

    firstField() {
      return this.fieldAt(1);
    },

    secondField() {
      return this.fieldAt(2);
    },

    thirdField() {
      return this.fieldAt(3);
    },

    hasSubConditions() {
      return !!(this.condition.children && this.condition.children.length);
    },

    subConditionsSummary() {
      const childrenCount = this.condition.children ? this.condition.children.length : 0;

      return childrenCount === 1
        ? '1 subregra recolhida'
        : `${childrenCount} subregras recolhidas`;
    },

    subConditionsToggleTitle() {
      return this.subConditionsExpanded
        ? 'Recolher subregras'
        : 'Expandir subregras';
    },
  },

  watch: {
    showFieldThree(showFieldThree) {
      if (showFieldThree) {
        this.ensureField(3);
      }
    },
  },

  created() {
    this.ensureConditionShape();
  },

  methods: {
    ensureConditionShape() {
      if (this.isSubCondition || !this.condition.type) this.$set(this.condition, 'type', 'AND');
      if (!this.condition.operator) this.$set(this.condition, 'operator', '=');
      if (!this.condition.dataSource) this.$set(this.condition, 'dataSource', 'registro_movimentacao');
      if (!Array.isArray(this.condition.children)) this.$set(this.condition, 'children', []);

      this.ensureFields();

      if (this.showFieldThree) {
        this.ensureField(3);
      }
    },

    ensureFields() {
      let fields = Array.isArray(this.condition.fields)
        ? [...this.condition.fields]
        : [];

      if (!fields.some((field) => field.order === 1)) {
        fields = [...fields, new Field(1)];
      }

      if (!fields.some((field) => field.order === 2)) {
        fields = [...fields, new Field(2)];
      }

      fields.sort((a, b) => a.order - b.order);
      this.$set(this.condition, 'fields', fields);
    },

    fieldAt(order) {
      if (!Array.isArray(this.condition.fields)) return null;

      return this.condition.fields.find((item) => item.order === order) || null;
    },

    ensureField(order) {
      this.ensureFields();

      const currentField = this.fieldAt(order);
      if (currentField) return currentField;

      const fields = [...this.condition.fields, new Field(order)]
        .sort((a, b) => a.order - b.order);
      this.$set(this.condition, 'fields', fields);

      return this.fieldAt(order);
    },

    handleOperatorChange() {
      this.ensureFields();
      this.resetComparisonFields();

      if (this.showFieldThree) {
        this.ensureField(3);

        return;
      }

      this.$set(
        this.condition,
        'fields',
        this.condition.fields.filter((field) => field.order !== 3),
      );
    },

    resetComparisonFields() {
      this.condition.fields
        .filter((field) => field.order > 1)
        .forEach((field) => {
          this.$set(field, 'value', '');
          this.$set(field, 'valuein', []);
          this.$set(field, 'parameters', []);
        });
    },

    addSubCondition() {
      const subCondition = new ConditionType();
      const nextOrder = this.condition.children.length + 1;
      subCondition.id = this.getNextConditionId(this.condition.children);
      subCondition.order = nextOrder;
      subCondition.isSubCondition = true;
      subCondition.fields = [new Field(1), new Field(2)];

      const children = [...this.condition.children, subCondition];

      this.reorderConditions(children);
      this.$set(this.condition, 'children', children);
      this.subConditionsExpanded = true;
    },

    removeSubCondition(conditionId) {
      const children = this.condition.children
        .filter((subCondition) => subCondition.id !== conditionId);

      this.reorderConditions(children);

      this.$set(this.condition, 'children', children);
    },

    reorderSubConditions() {
      this.reorderConditions(this.condition.children);
    },

    reorderConditions(conditions) {
      conditions.forEach((condition, index) => {
        this.$set(condition, 'order', index + 1);
        this.$set(condition, 'type', 'AND');
      });
    },

    toggleSubConditions() {
      this.subConditionsExpanded = !this.subConditionsExpanded;
    },

    getNextConditionId(conditions) {
      if (!conditions.length) return 1;

      return Math.max(...conditions.map((condition) => condition.id || 0)) + 1;
    },

    formatConditionType(conditionType) {
      if (conditionType === 'AND') return 'E';
      if (conditionType === 'OR') return 'OU';

      return conditionType;
    },
  },
};
</script>

<style scoped>
.compact-condition-editor {
  margin-bottom: 10px;
  width: 100%;
}

.compact-condition-editor__body {
  min-width: 0;
}

.compact-condition-editor__line {
  align-items: start;
  background: var(--v-muted-base);
  border: 1px solid var(--v-border-base);
  border-left: 4px solid var(--v-primary-base);
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(107, 107, 107, 0.12);
  display: grid;
  gap: 10px;
  grid-template-columns:
    24px
    78px
    minmax(150px, 0.58fr)
    minmax(250px, 1fr)
    minmax(145px, 0.48fr)
    minmax(250px, 1fr)
    96px;
  padding: 10px;
}

.compact-condition-editor--child {
  margin-bottom: 8px;
}

.compact-condition-editor--child .compact-condition-editor__line {
  background: var(--v-muted-base);
  border-left-color: var(--v-border-base);
}

.compact-condition-editor__drag-handle {
  align-items: center;
  cursor: grab;
  display: inline-flex;
  height: 40px;
  justify-content: center;
}

.compact-condition-editor__drag-handle:active {
  cursor: grabbing;
}

.compact-condition-editor__relation {
  align-items: flex-start;
  display: flex;
  height: 40px;
  min-width: 0;
}

.compact-condition-editor__type-toggle {
  background: var(--v-background-base);
  border: 1px solid var(--v-border-base);
  border-radius: 4px;
  overflow: hidden;
}

.compact-condition-editor__type-toggle ::v-deep .v-btn {
  color: var(--v-primary-base);
  font-weight: 700;
  min-height: 32px;
  min-width: 38px;
  padding-left: 8px;
  padding-right: 8px;
}

.compact-condition-editor__type-fixed {
  align-items: center;
  background: var(--v-secondary-base);
  border-radius: 4px;
  color: var(--v-foreground-accent-base);
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  height: 32px;
  justify-content: center;
  min-width: 38px;
}

.compact-condition-editor__source,
.compact-condition-editor__operator,
.compact-condition-editor__field {
  align-self: start;
  min-width: 0;
}

.compact-condition-editor__field--third {
  grid-column: 4 / span 3;
}

.compact-condition-editor__actions {
  align-items: center;
  display: flex;
  gap: 2px;
  grid-column: 7;
  grid-row: 1;
  height: 40px;
  justify-content: flex-end;
}

.compact-condition-editor__children-summary {
  background: var(--v-background-base);
  border: 1px dashed var(--v-border-base);
  border-radius: 6px;
  color: var(--v-muted-foreground-base);
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  margin-left: 40px;
  margin-top: 10px;
  padding: 8px 12px;
}

.compact-condition-editor__children-summary:hover {
  border-color: var(--v-primary-base);
  color: var(--v-primary-base);
}

.compact-condition-editor__children {
  border-left: 2px solid var(--v-border-base);
  margin-left: 40px;
  margin-top: 10px;
  padding-left: 14px;
}

.compact-condition-editor--ghost {
  opacity: 0.55;
}

.compact-condition-editor--ghost .compact-condition-editor__line {
  background: var(--v-border-accent-background-base);
  border-color: var(--v-border-accent-base);
}

@media (max-width: 1260px) {
  .compact-condition-editor__line {
    grid-template-columns: 24px 78px minmax(150px, 0.9fr) minmax(220px, 1fr) minmax(145px, 0.75fr) 96px;
  }

  .compact-condition-editor__field {
    grid-column: span 2;
  }

  .compact-condition-editor__field--third {
    grid-column: 3 / span 3;
  }

  .compact-condition-editor__actions {
    grid-column: 6;
  }
}

@media (max-width: 960px) {
  .compact-condition-editor,
  .compact-condition-editor__line {
    display: block;
  }

  .compact-condition-editor__drag-handle {
    display: none;
  }

  .compact-condition-editor__type-toggle,
  .compact-condition-editor__type-fixed,
  .compact-condition-editor__relation,
  .compact-condition-editor__source,
  .compact-condition-editor__operator,
  .compact-condition-editor__field,
  .compact-condition-editor__actions {
    margin-bottom: 8px;
    width: 100%;
  }

  .compact-condition-editor__actions {
    justify-content: flex-start;
  }

  .compact-condition-editor__children {
    margin-left: 8px;
    padding-left: 10px;
  }
}
</style>
